import { useState, useRef } from 'react';
import { exportAsImage, exportAsPDF, exportAsCSV, generateEmailApprovalLink } from '../../utils/export-helpers';
import { uploadCalendarData, uploadFileToServer } from '../../utils/server-upload';
import { useMutation } from './useMutation';
import type { Post, ExportData, ExportType, EmailData, ViewMode } from '../types';

interface UseCalendarExportProps {
  posts: Post[];
  displayMonths: string[];
  primaryMonth: string;
  viewMode: ViewMode;
}

export function useCalendarExport({ posts, displayMonths, primaryMonth, viewMode }: UseCalendarExportProps) {
  const [exportSuccess, setExportSuccess] = useState<string | null>(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportDialogType, setExportDialogType] = useState<ExportType | null>(null);
  const [exportDialogData, setExportDialogData] = useState<ExportData | null>(null);
  const [exportDialogEmailData, setExportDialogEmailData] = useState<EmailData | null>(null);
  const [currentUploadedUrl, setCurrentUploadedUrl] = useState<string | null>(null);

  const calendarRef = useRef<HTMLDivElement>(null);

  const prepareCalendarClone = async (calendarElement: HTMLDivElement): Promise<{ clone: HTMLDivElement; wrapper: HTMLDivElement }> => {
    // Find the active TabsContent in the original element
    const activeTabContent = calendarElement.querySelector('[role="tabpanel"][data-state="active"]') as HTMLElement | null;

    if (!activeTabContent) {
      throw new Error('No active tab content found');
    }

    // Clone only the active tab content
    const clone = activeTabContent.cloneNode(true) as HTMLDivElement;

    // Remove export toolbar if it exists in the clone
    const toolbarElements = clone.querySelectorAll('[class*="border-t"]');
    toolbarElements.forEach((toolbar) => {
      if (toolbar.querySelector('button') &&
          (toolbar.textContent?.includes('Download') ||
           toolbar.textContent?.includes('Export') ||
           toolbar.textContent?.includes('Send for Approval'))) {
        toolbar.remove();
      }
    });

    // Remove height constraints from the clone itself
    clone.style.height = 'auto';
    clone.style.maxHeight = 'none';
    clone.style.overflow = 'visible';

    // Find and expand all scrollable containers to show full content
    const cloneScrollContainer = clone.querySelector('[data-scroll-container]') as HTMLElement | null;
    if (cloneScrollContainer) {
      cloneScrollContainer.style.height = 'auto';
      cloneScrollContainer.style.maxHeight = 'none';
      cloneScrollContainer.style.overflow = 'visible';
    }

    // Find any overflow-y-auto elements and expand them
    const scrollableElements = clone.querySelectorAll('[class*="overflow-y-auto"]') as NodeListOf<HTMLElement>;
    scrollableElements.forEach((el) => {
      el.style.height = 'auto';
      el.style.maxHeight = 'none';
      el.style.overflow = 'visible';
    });

    // Create a wrapper with fixed positioning at 0,0 but invisible and no layout impact
    const wrapper = document.createElement('div');
    wrapper.style.position = 'fixed';
    wrapper.style.top = '0';
    wrapper.style.left = '0';
    wrapper.style.width = 'auto';
    wrapper.style.height = 'auto';
    wrapper.style.overflow = 'visible';
    wrapper.style.pointerEvents = 'none';
    wrapper.style.zIndex = '-9999';
    wrapper.style.opacity = '0';

    // Position clone absolutely within wrapper
    clone.style.position = 'relative';
    clone.style.top = '0';
    clone.style.left = '0';

    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);
    await new Promise(resolve => setTimeout(resolve, 200));

    return { clone, wrapper };
  };

  const getFilename = () => {
    return displayMonths.length > 1
      ? `${displayMonths[0]}_to_${displayMonths[displayMonths.length - 1]}`
      : primaryMonth;
  };

  const exportImageMutation = useMutation({
    mutationFn: async () => {
      if (!calendarRef.current) throw new Error('Calendar ref not available');
      const { clone, wrapper } = await prepareCalendarClone(calendarRef.current);
      const filename = getFilename();
      const exportData = await exportAsImage(clone, filename, viewMode);
      document.body.removeChild(wrapper);

      // Upload to server immediately
      try {
        const uploadResult = await uploadFileToServer({
          dataUrl: exportData.dataUrl,
          mimeType: 'image/png',
          filename: exportData.filename,
        });
        return { exportData, uploadUrl: uploadResult.url };
      } catch (error) {
        console.error('Failed to upload PNG to server:', error);
        return { exportData, uploadUrl: null };
      }
    },
    onSuccess: ({ exportData, uploadUrl }) => {
      setCurrentUploadedUrl(uploadUrl);
      setExportDialogType('png');
      setExportDialogData(exportData);
      setExportDialogOpen(true);
    },
    onError: (error) => {
      console.error('Failed to export as image:', error);
    },
  });

  const handleExportImage = () => {
    // Reset state to prevent showing stale data
    setCurrentUploadedUrl(null);
    setExportDialogData(null);
    setExportDialogType(null);
    exportImageMutation.mutate();
  };

  const exportPDFMutation = useMutation({
    mutationFn: async () => {
      if (!calendarRef.current) throw new Error('Calendar ref not available');
      const { clone, wrapper } = await prepareCalendarClone(calendarRef.current);
      const filename = getFilename();

      let exportData: ExportData;
      if (displayMonths.length > 1) {
        const monthSections = Array.from(clone.querySelectorAll('[data-month]')) as HTMLElement[];
        exportData = await exportAsPDF(clone, posts, filename, monthSections, viewMode);
      } else {
        exportData = await exportAsPDF(clone, posts, filename, undefined, viewMode);
      }

      document.body.removeChild(wrapper);

      // Upload to server immediately
      try {
        const uploadResult = await uploadFileToServer({
          dataUrl: exportData.dataUrl,
          mimeType: 'application/pdf',
          filename: exportData.filename,
        });
        return { exportData, uploadUrl: uploadResult.url };
      } catch (error) {
        console.error('Failed to upload PDF to server:', error);
        return { exportData, uploadUrl: null };
      }
    },
    onSuccess: ({ exportData, uploadUrl }) => {
      setCurrentUploadedUrl(uploadUrl);
      setExportDialogType('pdf');
      setExportDialogData(exportData);
      setExportDialogOpen(true);
    },
    onError: (error) => {
      console.error('Failed to export as PDF:', error);
    },
  });

  const handleExportPDF = () => {
    // Reset state to prevent showing stale data
    setCurrentUploadedUrl(null);
    setExportDialogData(null);
    setExportDialogType(null);
    exportPDFMutation.mutate();
  };

  const exportCSVMutation = useMutation({
    mutationFn: async () => {
      const filename = getFilename();
      const exportData = await exportAsCSV(posts, filename);

      // Upload CSV to server to get URL (required for ChatGPT sandbox)
      try {
        const uploadResult = await uploadFileToServer({
          dataUrl: exportData.dataUrl,
          mimeType: 'text/csv',
          filename: exportData.filename
        });
        return { exportData, uploadUrl: uploadResult.url };
      } catch (error) {
        console.error('Failed to upload CSV to server:', error);
        // Return CSV data without uploadUrl if upload fails
        return { exportData, uploadUrl: null };
      }
    },
    onSuccess: ({ exportData, uploadUrl }) => {
      setExportDialogType('csv');
      setExportDialogData(exportData);
      setCurrentUploadedUrl(uploadUrl);
      setExportDialogOpen(true);
    },
    onError: (error) => {
      console.error('Failed to export as CSV:', error);
    },
  });

  const handleExportCSV = () => {
    // Reset state to prevent showing stale PDF URL
    setCurrentUploadedUrl(null);
    setExportDialogData(null);
    setExportDialogType(null);
    exportCSVMutation.mutate();
  };

  const sendEmailMutation = useMutation({
    mutationFn: async () => {
      if (!calendarRef.current) throw new Error('Calendar ref not available');
      const { clone, wrapper } = await prepareCalendarClone(calendarRef.current);
      const filename = getFilename();

      let pdfData: ExportData;
      if (displayMonths.length > 1) {
        const monthSections = Array.from(clone.querySelectorAll('[data-month]')) as HTMLElement[];
        pdfData = await exportAsPDF(clone, posts, filename, monthSections, viewMode);
      } else {
        pdfData = await exportAsPDF(clone, posts, filename, undefined, viewMode);
      }

      document.body.removeChild(wrapper);

      // Upload to server immediately
      const uploadResult = await uploadFileToServer({
        dataUrl: pdfData.dataUrl,
        mimeType: 'application/pdf',
        filename: pdfData.filename,
      });

      const platformCount = new Set(posts.map(p => p.platform)).size;
      const emailData = generateEmailApprovalLink(primaryMonth, posts.length, platformCount);

      return { pdfData, emailData, uploadUrl: uploadResult.url };
    },
    onSuccess: ({ pdfData, emailData, uploadUrl }) => {
      setCurrentUploadedUrl(uploadUrl);
      setExportDialogType('email');
      setExportDialogData(pdfData);
      setExportDialogEmailData(emailData);
      setExportDialogOpen(true);
    },
    onError: (error) => {
      console.error('Failed to send email:', error);
    },
  });

  const handleSendEmail = () => {
    // Reset state to prevent showing stale data
    setCurrentUploadedUrl(null);
    setExportDialogData(null);
    setExportDialogType(null);
    sendEmailMutation.mutate();
  };

  const shareCalendarMutation = useMutation({
    mutationFn: async () => {
      const result = await uploadCalendarData({
        posts,
        month: primaryMonth,
      });
      return result;
    },
    onSuccess: (result) => {
      // Store shareable URL as exportData
      const exportData: ExportData = {
        dataUrl: result.url,
        filename: `calendar-${primaryMonth}.html`,
        text: result.url,
      };
      setExportDialogType('calendar-link');
      setExportDialogData(exportData);
      setCurrentUploadedUrl(result.url);
      setExportDialogOpen(true);
    },
    onError: (error) => {
      console.error('Failed to create shareable calendar:', error);
    },
  });

  const handleShareCalendar = () => {
    // Reset state to prevent showing stale data
    setCurrentUploadedUrl(null);
    setExportDialogData(null);
    setExportDialogType(null);
    shareCalendarMutation.mutate();
  };

  const isExporting =
    exportImageMutation.isLoading ||
    exportPDFMutation.isLoading ||
    exportCSVMutation.isLoading ||
    sendEmailMutation.isLoading ||
    shareCalendarMutation.isLoading;

  return {
    isExporting,
    exportSuccess,
    setExportSuccess,
    exportDialogOpen,
    setExportDialogOpen,
    exportDialogType,
    exportDialogData,
    exportDialogEmailData,
    currentUploadedUrl,
    calendarRef,
    handleExportImage,
    handleExportPDF,
    handleExportCSV,
    handleSendEmail,
    handleShareCalendar,
  };
}
